import { createSelector } from '@reduxjs/toolkit';

import { ReduxState } from 'app/redux/types';

import { AmountInputModifier, AmountValidationResult, SubmissionState } from '../../constants';
import { RecordOfRankEntryType } from '../../records';
import { RecordOfSystemStakeTypeState } from '../../types/stakeTypes/system';
import { selectBets } from '../entries';
import { selectStakeTypesState, selectSystemStakeTypeState } from '../state';

// ---

export const selectSystemSubmission = (state: ReduxState): SubmissionState => {
  return selectSystemStakeTypeState(state).get('submission');
};

export const selectSystemRank = (state: ReduxState): RecordOfSystemStakeTypeState['rank'] => {
  return selectSystemStakeTypeState(state).get('rank');
};

export const selectSystemRankOf = (state: ReduxState): RecordOfSystemStakeTypeState['rankOf'] => {
  return selectSystemStakeTypeState(state).get('rankOf');
};

export const selectSystemAmountModifier = (state: ReduxState): AmountInputModifier => {
  return selectSystemStakeTypeState(state).get('amountModifier');
};

export const selectSystemRankEntries = (
  state: ReduxState,
  rank: number,
): RecordOfRankEntryType | undefined => {
  return selectSystemStakeTypeState(state).get('rankEntries').get(rank);
};

export const selectSystemRankEntry = (state: ReduxState): RecordOfRankEntryType | null => {
  const rank = selectSystemRank(state);

  if (!rank) {
    return null;
  }

  const rankEntries = selectSystemRankEntries(state, rank);

  if (!rankEntries) {
    return null;
  }

  return rankEntries;
};

/**
 * Recursive algorithm for combinations without repetitions
 */
function combineWithoutRepetitions(comboOptions: number[], comboLength: number): (number[])[] {
  // If the length of the combination is 1 then each element of the original array
  // is a combination itself.
  if (comboLength === 1) {
    return comboOptions.map((comboOption) => [comboOption]);
  }

  // Init combinations array.
  const combos: (number[])[] = [];

  // Extract characters one by one and concatenate them to combinations of smaller lengths.
  // We need to extract them because we don't want to have repetitions after concatenation.
  comboOptions.forEach((currentOption, optionIndex) => {
    // Generate combinations of smaller size.
    const smallerCombos = combineWithoutRepetitions(
      comboOptions.slice(optionIndex + 1),
      comboLength - 1,
    );

    // Concatenate currentOption with all combinations of smaller size.
    smallerCombos.forEach((smallerCombo) => {
      combos.push([currentOption].concat(smallerCombo));
    });
  });

  return combos;
}

/**
 * Calculate total price for stake type of "system".
 */
export const selectSystemPrice = createSelector(
  selectSystemRank,
  selectBets,
  (rank, bets) => {
    if (!rank) {
      return null;
    }

    const betIds = bets.keySeq().toArray();
    const combos = combineWithoutRepetitions(betIds, rank);

    return combos.reduce((accumulator, combo) => {
      return accumulator + combo.reduce((expressPrice, entryIndex) => {
        return expressPrice * (bets.get(entryIndex)?.price || 0);
      }, 1);
    }, 0) / combos.length;
  },
);

/**
 * Calculate resulting (modified) amount for stake type of "system".
 */
export const selectSystemResultingAmount = createSelector(
  selectSystemAmountModifier,
  (state: ReduxState) => selectStakeTypesState(state).amount,
  (state: ReduxState) => selectSystemRankEntry(state)?.combinations,
  (modifier, systemAmount, combinations) => {
    if (Number.isNaN(systemAmount.numeric) || !combinations) {
      return null;
    }

    switch (modifier) {
      case AmountInputModifier.DIVIDE:
        // do nothing with value
        return systemAmount.numeric;

      case AmountInputModifier.MULTIPLY:
        return systemAmount.numeric * combinations;

      default:
        console.error(`selectSystemResultingAmount -> unrecognized amount modifier [${modifier}]`);
        return null;
    }
  },
);

/**
 * Calculate max winning for stake type of "system".
 */
export const selectSystemMaxWinning = (state: ReduxState): number => {
  const price = selectSystemPrice(state);
  const isToggle = selectSystemAmountModifier(state) === AmountInputModifier.MULTIPLY;
  const combinations = selectSystemRankEntry(state)?.combinations;

  if (typeof price !== 'number') {
    return 0;
  }

  const amount = selectStakeTypesState(state).amount;

  if (Number.isNaN(amount.numeric)) {
    return 0;
  }

  if (isToggle && combinations) {
    return price * amount.numeric * combinations;
  }

  return price * amount.numeric;
};

/**
 * Validate amount of the stake type of "system".
 */
export const selectValidateSystemAmount = (state: ReduxState): AmountValidationResult => {
  const amount = selectStakeTypesState(state).amount;

  if (Number.isNaN(amount.numeric)) {
    return AmountValidationResult.EMPTY_VALUE;
  }

  const rank = selectSystemRank(state);

  if (!rank) {
    return AmountValidationResult.NUMBER_OF_STAKES_NOT_SELECTED;
  }

  const rankEntries = selectSystemRankEntries(state, rank);

  if (!rankEntries) {
    return AmountValidationResult.NO_ERRORS;
  }

  if (amount.numeric < rankEntries.min) {
    return AmountValidationResult.MIN_NOT_REACHED;
  }

  if (amount.numeric > rankEntries.max) {
    return AmountValidationResult.BIG_STAKE;
  }

  return AmountValidationResult.NO_ERRORS;
};
